import React, { useState, useEffect } from 'react';
import { dashboardAPI } from '../services/api';
import { useGroup } from '../contexts/GroupContext';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import ErrorMessage from '../components/Common/ErrorMessage';
import { formatTimestamp } from '../utils/helpers';
import { Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import {
  IdCardIcon,
  CalendarIcon,
  ChartIcon,
  UsersIcon,
  ApiIcon,
  DocumentIcon
} from '../components/Common/Icons';

const Dashboard = () => {
  const { selectedGroupId, selectedGroup } = useGroup();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  

  useEffect(() => {
    fetchDashboardData();
  }, [selectedGroupId]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await dashboardAPI.getStats();
      setStats(response.data.data);
    } catch (err) {
      setError('Failed to load dashboard data');
      console.error('Error fetching dashboard data:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <LoadingSpinner text="Loading dashboard..." />;
  }

  if (error) {
    return <ErrorMessage message={error} onClose={() => setError(null)} />;
  }

  if (!stats) {
    return <ErrorMessage message="No data available" />;
  }

  // Prepare chart data
  const documentStatusData = [
    { name: 'Active', value: stats.documents?.active || 0, color: '#10B981' },
    { name: 'Revoked', value: stats.documents?.revoked || 0, color: '#EF4444' },
    { name: 'Expired', value: stats.documents?.expired || 0, color: '#F59E0B' }
  ].filter(item => item.value > 0);
 

  const statCards = [
    {
      title: 'Total ID Cards Issued',
      value: stats.documents?.total || 0,
      icon: IdCardIcon,
      color: 'blue',
      subtitle: 'All time'
    },
    {
      title: 'ID Cards Issued Today',
      value: stats.documents?.today || 0,
      icon: CalendarIcon,
      color: 'green',
      subtitle: 'Last 24 hours'
    },
    {
      title: 'ID Cards This Week',
      value: stats.documents?.thisWeek || 0,
      icon: CalendarIcon,
      color: 'info',
      subtitle: 'Last 7 days'
    },
    {
      title: 'ID Cards This Month',
      value: stats.documents?.thisMonth || 0,
      icon: ChartIcon,
      color: 'success',
      subtitle: 'Current month'
    },
    {
      title: 'Active Documents',
      value: stats.documents?.active || 0,
      icon: DocumentIcon,
      color: 'success',
      subtitle: 'Currently active'
    },
    {
      title: 'Revoked Documents',
      value: stats.documents?.revoked || 0,
      icon: DocumentIcon,
      color: 'danger',
      subtitle: 'Revoked status'
    },
    {
      title: 'Total Employees',
      value: stats.employees?.total || 0,
      icon: UsersIcon,
      color: 'blue',
      subtitle: 'Registered employees'
    },
    {
      title: 'Total API Requests',
      value: stats.apiRequests?.total || 0,
      icon: ApiIcon,
      color: 'info',
      subtitle: 'All time requests'
    },
    {
      title: 'API Success Rate',
      value: `${stats.apiRequests?.successRate || 0}%`,
      icon: ChartIcon,
      color: stats.apiRequests?.successRate >= 90 ? 'success' : stats.apiRequests?.successRate >= 70 ? 'warning' : 'danger',
      subtitle: `${stats.apiRequests?.successful || 0} successful`
    },
    {
      title: 'API Requests Today',
      value: stats.apiRequests?.today || 0,
      icon: ApiIcon,
      color: 'info',
      subtitle: 'Last 24 hours'
    }
  ];

  return (
    <div className="dashboard">
      <div className="dashboard-header-modern">
        <div className="dashboard-header-content">
          <div>
            <h1 className="dashboard-title">DigiLocker Issuer</h1>
            <p className="dashboard-subtitle">
              Overview of operations and key metrics
              {selectedGroup && (
                <span className="dashboard-group-badge">
                  • {selectedGroup.name}
                </span>
              )}
            </p>
          </div>
        </div>
      </div>

      <div className="stats-grid-modern">
        {statCards.map((card, index) => {
          const Icon = card.icon;
          return (
            <div key={index} className={`stat-card-modern stat-card-${card.color}`}>
              <div className="stat-card-header">
                <div className="stat-icon-modern">
                  <Icon className="stat-icon-svg" />
                </div>
                <div className="stat-content-modern">
                  <h3 className="stat-value-modern">{card.value}</h3>
                  <p className="stat-title-modern">{card.title}</p>
                </div>
              </div>
              {card.subtitle && <p className="stat-subtitle-modern">{card.subtitle}</p>}
            </div>
          );
        })}
      </div>

      {/* Main operational vs analytics layout */}
      <div className="dashboard-main-grid">
        {/* LEFT: operational data (tables) */}
        <div className="dashboard-main-left">
          <div className="content-section">
            <h3>Recent ID Card Issuances</h3>
            <div className="recent-activity">
              {stats.recentDocuments && stats.recentDocuments.length > 0 ? (
                <table className="recent-table">
                  <thead>
                    <tr>
                      <th>Document ID</th>
                      <th>Employee</th>
                      {/* <th>Type</th> */}
                      <th>Status</th>
                      <th>Issued</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.recentDocuments.map((doc) => (
                      <tr key={doc.id}>
                        <td>{doc.doc_id}</td>
                        <td>{doc.employee?.full_name || 'N/A'}</td>
                        {/* <td>{doc.doc_type}</td> */}
                        <td>
                          <span className={`status-badge status-${doc.status}`}>
                            {doc.status}
                          </span>
                        </td>
                        <td>{formatTimestamp(doc.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p>No recent document issuances.</p>
              )}
            </div>
          </div>

          <div className="content-section">
            <h3>Recent API Requests</h3>
            <div className="recent-activity">
              {stats.recentApiRequests && stats.recentApiRequests.length > 0 ? (
                <table className="recent-table">
                  <thead>
                    <tr>
                      <th>Transaction ID</th>
                      <th>Doc Type</th>
                      <th>Status</th>
                      <th>Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.recentApiRequests.map((req) => (
                      <tr key={req.id}>
                        <td>{req.txn_id}</td>
                        <td>{req.doc_type}</td>
                        <td>
                          <span className={`status-badge ${req.status_returned === 1 || req.http_status === 200 ? 'status-success' : 'status-error'}`}>
                            {req.status_returned === 1 || req.http_status === 200 ? 'Success' : 'Failed'}
                          </span>
                        </td>
                        <td>{formatTimestamp(req.requested_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p>No recent API requests.</p>
              )}
            </div>
          </div>
        </div>

        {/* RIGHT: analytics & metrics */}
        <div className="dashboard-main-right">
          <div className="chart-card">
            <div className="chart-header">
              <h3 className="chart-title">Document Status Distribution</h3>
              <span className="chart-subtitle">Current breakdown</span>
            </div>
            <div className="chart-container">
              {documentStatusData.length > 0 ? (
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie
                      data={documentStatusData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                      outerRadius={90}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {documentStatusData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#fff', 
                        border: '1px solid #E5E7EB', 
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                      }} 
                    />
                    <Legend 
                      verticalAlign="bottom" 
                      height={30}
                      iconType="circle"
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="chart-empty-state">
                  <p>No document data available</p>
                </div>
              )}
            </div>
          </div>
          <div className="stats-card-modern">
            <h3 className="stats-card-title">API Request Statistics</h3>
            <div className="api-stats-modern">
              <div className="api-stat-item-modern">
                <span className="api-stat-label-modern">Total Requests</span>
                <span className="api-stat-value-modern">{stats.apiRequests?.total || 0}</span>
              </div>
              <div className="api-stat-item-modern">
                <span className="api-stat-label-modern">Successful</span>
                <span className="api-stat-value-modern api-success-modern">{stats.apiRequests?.successful || 0}</span>
              </div>
              <div className="api-stat-item-modern">
                <span className="api-stat-label-modern">Failed</span>
                <span className="api-stat-value-modern api-failed-modern">{stats.apiRequests?.failed || 0}</span>
              </div>
              <div className="api-stat-item-modern">
                <span className="api-stat-label-modern">This Week</span>
                <span className="api-stat-value-modern">{stats.apiRequests?.thisWeek || 0}</span>
              </div>
              <div className="api-stat-item-modern">
                <span className="api-stat-label-modern">This Month</span>
                <span className="api-stat-value-modern">{stats.apiRequests?.thisMonth || 0}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <footer className="dashboard-footer">
        <div className="footer-content">
          <p className="footer-company">Navantra Global Solutions Pvt Ltd</p>
          <p className="footer-copyright">All rights reserved by Navantra</p>
        </div>
      </footer>
    </div>
  );
};

export default Dashboard;
